import React, { useEffect, useState, useCallback, useRef } from "react";
import { MDBContainer } from "mdb-react-ui-kit";
import { useAuth } from "../context/AuthContext";
import "./ViewProductsPage.css";

const BASE_URL =
  "https://clextensions-a6bvf6amfrc9aeex.centralindia-01.azurewebsites.net";

const RetryImage = ({ src, alt, className }) => {
  const imgRef = useRef(null);
  const retryCount = useRef(0);
  const MAX_RETRIES = 3;
  const RETRY_DELAY = 2000;

  const handleError = () => {
    if (retryCount.current < MAX_RETRIES) {
      retryCount.current += 1;
      setTimeout(() => {
        if (imgRef.current) {
          imgRef.current.src =
            src + (src.includes("?") ? "&" : "?") + "t=" + Date.now();
        }
      }, RETRY_DELAY * retryCount.current);
    } else {
      if (imgRef.current) {
        imgRef.current.onerror = null;
        imgRef.current.src = "https://via.placeholder.com/120";
      }
    }
  };

  return (
    <img
      ref={imgRef}
      src={src}
      alt={alt}
      className={className}
      loading="lazy"
      onError={handleError}
    />
  );
};

const VintedProductsPage = () => {
  const { user, companyId } = useAuth();
// eslint-disable-next-line no-unused-vars
  const userId = user?.id || user?.user_id || user?.userId;

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState("");
  const [brand, setBrand] = useState("");
  const [condition, setCondition] = useState("");
  const [size, setSize] = useState("");
  const [status, setStatus] = useState("");
  const [profileUsername, setProfileUsername] = useState("");
  const [uploadDate, setUploadDate] = useState("");
  const [priceMin, setPriceMin] = useState("");
  const [priceMax, setPriceMax] = useState("");
  const [viewsMin, setViewsMin] = useState("");
  const [viewsMax, setViewsMax] = useState("");
  const [favMin, setFavMin] = useState("");
  const [favMax, setFavMax] = useState("");
  const [sort, setSort] = useState("scraped_at");
  const [order, setOrder] = useState("desc");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit, setLimit] = useState(20);
  const [profiles, setProfiles] = useState([]);

  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [updatingAllDates, setUpdatingAllDates] = useState(false);

  const [sellerMap, setSellerMap] = useState({});
  const [sellerFilter, setSellerFilter] = useState("");
  const [allSellers, setAllSellers] = useState([]); // 🔥 saare sellers

  // 1. Fetch profiles dropdown
  useEffect(() => {
    fetch(`${BASE_URL}/api/vinted-profiles`)
      .then((res) => res.json())
      .then((data) => {
        if (data.success) setProfiles(data.profiles);
      })
      .catch((err) => console.error("Failed to fetch profiles", err));
  }, []);

  // 2. 🔥 Fetch ALL distinct sellers on mount
  useEffect(() => {
    fetch(`${BASE_URL}/api/vinted-sellers`)
      .then(res => res.json())
      .then(data => {
        if (data.success) setAllSellers(data.sellers);
      })
      .catch(err => console.error("Failed to fetch sellers:", err));
  }, []);

  // 3. Seller map for current page's products (badges ke liye)
  const fetchSellerMap = useCallback(async (productList) => {
    const uniqueTitles = [
      ...new Set(
        productList.map((p) => (p.title || "").trim()).filter(Boolean)
      ),
    ];
    if (uniqueTitles.length === 0) {
      setSellerMap({});
      return;
    }
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${BASE_URL}/api/products-seller-map-by-title`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ titles: uniqueTitles }),
      });
      const data = await res.json();
      if (data.success) {
        setSellerMap(data.data);
      }
    } catch (err) {
      console.error("Failed to fetch seller map:", err);
    }
  }, []);

  // 4. 🔥 fetchProducts – ab seller_name bhi bhejo (server-side filter)
  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page,
        limit,
        sort,
        order,
        search,
        brand,
        condition,
        size,
        status,
        profile_username: profileUsername,
        upload_date: uploadDate,
        price_min: priceMin,
        price_max: priceMax,
        views_min: viewsMin,
        views_max: viewsMax,
        favourites_min: favMin,
        favourites_max: favMax,
      });

      if (sellerFilter) {
        params.append("seller_name", sellerFilter);
      }

      const res = await fetch(`${BASE_URL}/api/vinted-products?${params}`);
      const data = await res.json();
      if (data.success) {
        setProducts(data.data);
        setTotalPages(data.totalPages);
        fetchSellerMap(data.data);
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [
    page, limit, sort, order, search, brand, condition, size, status,
    profileUsername, uploadDate, priceMin, priceMax, viewsMin, viewsMax,
    favMin, favMax, sellerFilter, fetchSellerMap,
  ]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const getSellerInfo = (product) => {
    const key = (product.title || "").trim();
    return sellerMap[key] || null;
  };

  // Bulk actions (unchanged)
  const handleBulkDeleteVinted = async () => {
    if (selectedProductIds.length === 0) return;
    if (!companyId) return;
    if (!window.confirm(`Delete ${selectedProductIds.length} selected items?`)) return;

    setDeleteLoading(true);
    try {
      await fetch(`${BASE_URL}/api/set-user-id`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: companyId }),
      });
      await new Promise((resolve) => setTimeout(resolve, 2000));

      const res = await fetch(`${BASE_URL}/api/bulk-delete-vinted`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: companyId, productIds: selectedProductIds }),
      });
      const data = await res.json();
      if (data.success) {
        alert(`Deleted: ${data.message}`);
        setSelectedProductIds([]);
        fetchProducts();
      } else {
        alert("Delete failed: " + data.error);
      }
    } catch (err) {
      alert("Network error: " + err.message);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleUpdateAllUploadDates = async () => {
    if (!companyId) return;
    if (!window.confirm("This will re-scrape upload dates for ALL products. Continue?")) return;
    setUpdatingAllDates(true);
    try {
      const res = await fetch(`${BASE_URL}/api/update-all-upload-dates`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: companyId }),
      });
      const data = await res.json();
      if (data.success) {
        alert(`Upload dates updated. Updated: ${data.updated}, Skipped: ${data.skipped}`);
        fetchProducts();
      } else {
        alert("Update failed: " + (data.message || data.error || "Unknown error"));
      }
    } catch (err) {
      alert("Network error: " + err.message);
    } finally {
      setUpdatingAllDates(false);
    }
  };

  const handleBulkUploadVinted = async () => {
    if (selectedProductIds.length === 0) return;
    if (!companyId) return;

    setBulkUploading(true);
    try {
      await fetch(`${BASE_URL}/api/set-user-id`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: companyId }),
      });
      await new Promise((resolve) => setTimeout(resolve, 2000));

      const res = await fetch(`${BASE_URL}/api/bulk-upload-vinted`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: companyId, productIds: selectedProductIds }),
      });
      const data = await res.json();
      if (data.success) {
        alert(`Bulk upload started. ${data.processed} products queued.`);
        setSelectedProductIds([]);
      } else {
        alert("Bulk upload failed: " + data.error);
      }
    } catch (err) {
      alert("Network error: " + err.message);
    } finally {
      setBulkUploading(false);
    }
  };

  const handleSelectProduct = (productId) => {
    setSelectedProductIds((prev) =>
      prev.includes(productId)
        ? prev.filter((id) => id !== productId)
        : [...prev, productId]
    );
  };

  const handleSelectAll = () => {
    if (products.length === 0) return;
    const allIds = products.map((p) => p.product_id);
    const allSelected = allIds.every((id) => selectedProductIds.includes(id));
    if (allSelected) {
      setSelectedProductIds((prev) => prev.filter((id) => !allIds.includes(id)));
    } else {
      setSelectedProductIds((prev) => [...new Set([...prev, ...allIds])]);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchProducts();
  };

  const handleClearFilters = () => {
    setSearch("");
    setBrand("");
    setCondition("");
    setSize("");
    setStatus("");
    setProfileUsername("");
    setUploadDate("");
    setPriceMin("");
    setPriceMax("");
    setViewsMin("");
    setViewsMax("");
    setFavMin("");
    setFavMax("");
    setSort("scraped_at");
    setOrder("desc");
    setPage(1);
    setSellerFilter("");
  };

  return (
    <MDBContainer fluid className="view-products-container">
      <h2 className="mb-4">Vinted Products</h2>

      <div className="mb-3">
        <button
          type="button"
          className="btn btn-warning"
          onClick={handleUpdateAllUploadDates}
          disabled={updatingAllDates}
        >
          {updatingAllDates ? "Updating..." : "Refresh All Missing Upload Dates"}
        </button>
        <small className="text-muted ms-2">
          Updates upload dates for products that do not have one yet.
        </small>
      </div>

      {selectedProductIds.length > 0 && (
        <div className="selection-bar">
          <span className="selection-count">
            <strong>{selectedProductIds.length}</strong> product(s) selected
          </span>
          <button
            type="button"
            className="btn btn-link btn-sm"
            onClick={() => setSelectedProductIds([])}
          >
            Clear Selection
          </button>
          <button
            type="button"
            className="btn btn-info btn-sm ms-2"
            onClick={handleBulkUploadVinted}
            disabled={bulkUploading}
          >
            {bulkUploading
              ? "Uploading..."
              : `Bulk Upload to Vinted (${selectedProductIds.length})`}
          </button>
          <button
            type="button"
            className="btn btn-danger btn-sm ms-2"
            onClick={handleBulkDeleteVinted}
            disabled={deleteLoading}
          >
            {deleteLoading ? "Deleting..." : `Bulk Delete (${selectedProductIds.length})`}
          </button>
        </div>
      )}

      <div className="filters-section">
        <form onSubmit={handleSearch} className="filters-grid">
          <div className="filter-item">
            <label>Search</label>
            <input
              className="filter-input"
              placeholder="Title, Brand, ID..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="filter-item">
            <label>Brand</label>
            <input
              className="filter-input"
              placeholder="e.g. Nike"
              value={brand}
              onChange={(e) => setBrand(e.target.value)}
            />
          </div>

          <div className="filter-item">
            <label>Condition</label>
            <select
              className="filter-select"
              value={condition}
              onChange={(e) => setCondition(e.target.value)}
            >
              <option value="">All</option>
              <option value="New with tags">New with tags</option>
              <option value="Very good">Very good</option>
              <option value="Good">Good</option>
              <option value="Satisfactory">Satisfactory</option>
            </select>
          </div>

          <div className="filter-item">
            <label>Size</label>
            <input
              className="filter-input"
              placeholder="e.g. L, 42"
              value={size}
              onChange={(e) => setSize(e.target.value)}
            />
          </div>

          <div className="filter-item">
            <label>Status</label>
            <select
              className="filter-select"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="">All</option>
              <option value="Active">Active</option>
              <option value="Sold">Sold</option>
              <option value="Draft">Draft</option>
            </select>
          </div>

          <div className="filter-item">
            <label>Profile Username</label>
            <select
              className="filter-select"
              value={profileUsername}
              onChange={(e) => setProfileUsername(e.target.value)}
            >
              <option value="">All Profiles</option>
              {profiles.map((p) => (
                <option key={p.username} value={p.username}>
                  {p.username}
                </option>
              ))}
            </select>
          </div>

          {/* 🔥 SELLER DROPDOWN – ab allSellers se populate */}
          <div className="filter-item">
            <label>Seller (ERP)</label>
            <select
              className="filter-select"
              value={sellerFilter}
              onChange={(e) => {
                setSellerFilter(e.target.value);
                setPage(1); // reset page
              }}
            >
              <option value="">All Sellers</option>
              {allSellers.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-item">
            <label>Upload Date</label>
            <input
              className="filter-input"
              placeholder="e.g. 2 days ago, a week ago"
              value={uploadDate}
              onChange={(e) => setUploadDate(e.target.value)}
            />
          </div>

          <div className="filter-item">
            <label>Min Price</label>
            <input
              type="number"
              className="filter-input"
              placeholder="0"
              value={priceMin}
              onChange={(e) => setPriceMin(e.target.value)}
            />
          </div>

          <div className="filter-item">
            <label>Max Price</label>
            <input
              type="number"
              className="filter-input"
              placeholder="999"
              value={priceMax}
              onChange={(e) => setPriceMax(e.target.value)}
            />
          </div>

          <div className="filter-item">
            <label>Min Views</label>
            <input
              type="number"
              className="filter-input"
              placeholder="0"
              value={viewsMin}
              onChange={(e) => setViewsMin(e.target.value)}
            />
          </div>

          <div className="filter-item">
            <label>Max Views</label>
            <input
              type="number"
              className="filter-input"
              placeholder="9999"
              value={viewsMax}
              onChange={(e) => setViewsMax(e.target.value)}
            />
          </div>

          <div className="filter-item">
            <label>Min Favorites</label>
            <input
              type="number"
              className="filter-input"
              placeholder="0"
              value={favMin}
              onChange={(e) => setFavMin(e.target.value)}
            />
          </div>

          <div className="filter-item">
            <label>Max Favorites</label>
            <input
              type="number"
              className="filter-input"
              placeholder="9999"
              value={favMax}
              onChange={(e) => setFavMax(e.target.value)}
            />
          </div>

          <div className="filter-item">
            <label>Sort by</label>
            <select
              className="filter-select"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
            >
              <option value="scraped_at">Last Scraped</option>
              <option value="price">Price</option>
              <option value="views">Views</option>
              <option value="favourites">Favorites</option>
              <option value="upload_date">Upload Date</option>
            </select>
          </div>

          <div className="filter-item">
            <label>Order</label>
            <select
              className="filter-select"
              value={order}
              onChange={(e) => setOrder(e.target.value)}
            >
              <option value="desc">Descending</option>
              <option value="asc">Ascending</option>
            </select>
          </div>

          <div className="filter-item">
            <label>Items per page</label>
            <select
              className="filter-select"
              value={limit}
              onChange={(e) => {
                setLimit(parseInt(e.target.value, 10));
                setPage(1);
              }}
            >
              <option value={20}>20 per page</option>
              <option value={50}>50 per page</option>
              <option value={100}>100 per page</option>
            </select>
          </div>

          <div className="filter-item" style={{ alignSelf: "flex-end" }}>
            <button type="submit" className="btn btn-outline-primary me-2">
              Search
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleClearFilters}
            >
              Clear
            </button>
          </div>
        </form>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {sellerFilter && (
        <div className="active-filters mb-2">
          <small className="text-muted">
            Filtered by seller: <strong>{sellerFilter}</strong>
          </small>
        </div>
      )}

      {loading ? (
        <div className="py-5 text-center">
          <h4>Loading Vinted products...</h4>
        </div>
      ) : (
        <>
          {products.length > 0 && (
            <div className="select-all-row">
              <input
                type="checkbox"
                id="selectAll"
                checked={
                  products.length > 0 &&
                  products.every((p) =>
                    selectedProductIds.includes(p.product_id)
                  )
                }
                onChange={handleSelectAll}
              />
              <label htmlFor="selectAll">Select All (current page)</label>
            </div>
          )}

          <div className="product-list">
            {products.length === 0 ? (
              <p className="text-center">No products found.</p>
            ) : (
              products.map((p) => {
                const sellerInfo = getSellerInfo(p);
                const sellerName =
                  sellerInfo && sellerInfo.seller_name
                    ? sellerInfo.seller_name
                    : "Unknown Seller";
                const isAmbiguous = Boolean(sellerInfo && sellerInfo.ambiguous);

                return (
                  <div
                    key={p.id}
                    className={`product-card ${
                      selectedProductIds.includes(p.product_id) ? "selected" : ""
                    }`}
                  >
                    <div className="product-main">
                      <div className="product-main-content">
                        <div className="product-checkbox">
                          <input
                            type="checkbox"
                            checked={selectedProductIds.includes(p.product_id)}
                            onChange={() => handleSelectProduct(p.product_id)}
                          />
                        </div>

                        <RetryImage
                          src={p.image_url || "https://via.placeholder.com/120"}
                          alt={p.title}
                          className="product-image"
                        />

                        <div className="product-info">
                          <h5 className="product-title">{p.title}</h5>

                          <div className="product-badges">
                            <span className="product-badge">Price: {p.price}</span>
                            <span className="product-badge">
                              Brand: {p.brand || "N/A"}
                            </span>
                            <span className="product-badge">
                              Condition: {p.condition_status}
                            </span>
                            <span className="product-badge">
                              Size: {p.size || "N/A"}
                            </span>
                            <span className="product-badge">Views: {p.views}</span>
                            <span className="product-badge">
                              Favorites: {p.favourites}
                            </span>
                            <span className="product-badge">
                              Upload Date: {p.upload_date || "N/A"}
                            </span>
                            <span
                              className={`product-badge ${
                                p.status === "Sold"
                                  ? "bg-danger"
                                  : p.status === "Draft"
                                  ? "bg-warning"
                                  : "bg-success"
                              }`}
                            >
                              {p.status || "Active"}
                            </span>
                          </div>

                          <div className="marketplace-badges">
                            <span
                              className="product-badge"
                              title={
                                isAmbiguous
                                  ? "Title ERP mein 2 ya zyada baar mili hai, manually verify karo"
                                  : ""
                              }
                              style={
                                isAmbiguous
                                  ? { border: "1px solid #dc3545" }
                                  : undefined
                              }
                            >
                              Seller: {sellerName}
                              {isAmbiguous ? " (check)" : ""}
                            </span>
                            <span className="product-badge">
                              Profile: {p.profile_username}
                            </span>
                            <a
                              href={`https://www.vinted.com/items/${p.product_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-sm btn-info ms-2"
                            >
                              View on Vinted
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {totalPages > 1 && (
            <div className="pagination mt-3 justify-content-center">
              <button
                className="btn btn-outline-primary me-2"
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
              >
                Previous
              </button>
              <span className="align-self-center">
                Page {page} of {totalPages}
              </span>
              <button
                className="btn btn-outline-primary ms-2"
                disabled={page === totalPages}
                onClick={() => setPage(page + 1)}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </MDBContainer>
  );
};

export default VintedProductsPage;